import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import {
  createDosingLog,
  updateDosingLog,
  type DosingLog,
} from '../../services/dosingLogService';
import type { DosingSchedule } from '../../services/dosingScheduleService';
import { parseDecimal } from './DosingScheduleForm';

/** Log a dose now (optionally for a plan, prefilled), or correct a logged dose. */
export type DosingLogFormMode =
  | { readonly kind: 'create'; readonly schedule?: DosingSchedule }
  | { readonly kind: 'edit'; readonly log: DosingLog };

export interface DosingLogFormProps {
  aquariumId: string;
  mode: DosingLogFormMode;
  onSuccess: (log: DosingLog) => void;
  onCancel: () => void;
}

type FieldKey = 'productName' | 'doseAmount' | 'doseUnit' | 'notes';

export function DosingLogForm({ aquariumId, mode, onSuccess, onCancel }: DosingLogFormProps) {
  const { t } = useTranslation();
  const isEdit = mode.kind === 'edit';
  const source = mode.kind === 'edit' ? mode.log : mode.schedule;

  const [productName, setProductName] = useState(source?.product_name ?? '');
  const [doseAmount, setDoseAmount] = useState(source ? String(source.dose_amount) : '');
  const [doseUnit, setDoseUnit] = useState(source?.dose_unit ?? '');
  const [notes, setNotes] = useState(mode.kind === 'edit' ? mode.log.notes ?? '' : '');
  const [errors, setErrors] = useState<Partial<Record<FieldKey, true>>>({});
  const [saveFailed, setSaveFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveFailed(false);
    const amount = parseDecimal(doseAmount);
    const next: Partial<Record<FieldKey, true>> = {};
    if (!isEdit && productName.trim().length === 0) next.productName = true;
    if (!Number.isFinite(amount) || amount <= 0 || amount > 99999) next.doseAmount = true;
    if (!isEdit && (doseUnit.trim().length === 0 || doseUnit.trim().length > 20)) next.doseUnit = true;
    if (notes.length > 2000) next.notes = true;
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const trimmedNotes = notes.trim();
    setIsSubmitting(true);
    try {
      const saved =
        mode.kind === 'edit'
          ? await updateDosingLog(aquariumId, mode.log.id, {
              dose_amount: amount,
              notes: trimmedNotes.length > 0 ? trimmedNotes : null,
            })
          : await createDosingLog(aquariumId, {
              product_name: productName.trim(),
              dose_amount: amount,
              dose_unit: doseUnit.trim(),
              schedule_id: mode.schedule?.id ?? null,
              notes: trimmedNotes.length > 0 ? trimmedNotes : null,
            });
      onSuccess(saved);
    } catch {
      setSaveFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="dosing-log-form" onSubmit={handleSubmit} noValidate>
      <Stack gap="4">
        {saveFailed && (
          <Alert tone="danger" title={t('dosing.logForm.saveErrorTitle')} data-testid="dosing-log-form-save-error">
            {t('dosing.logForm.saveError')}
          </Alert>
        )}

        {mode.kind === 'create' && mode.schedule?.dosing_pump && (
          <Alert tone="info" live="off" compact>
            {t('dosing.logForm.pumpExtraHint')}
          </Alert>
        )}

        {isEdit ? (
          <Text>
            <strong>{t('dosing.form.productName')}:</strong> {productName} ({doseUnit})
          </Text>
        ) : (
          <>
            <Input
              id="dosing-log-product-name"
              label={t('dosing.form.productName')}
              value={productName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setProductName(e.target.value)}
              error={errors.productName ? t('dosing.form.errors.productName') : undefined}
            />
          </>
        )}

        <Input
          id="dosing-log-dose-amount"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          label={t('dosing.form.doseAmount')}
          value={doseAmount}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setDoseAmount(e.target.value)}
          error={errors.doseAmount ? t('dosing.form.errors.doseAmount') : undefined}
        />

        {!isEdit && (
          <Input
            id="dosing-log-dose-unit"
            label={t('dosing.form.doseUnit')}
            value={doseUnit}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setDoseUnit(e.target.value)}
            error={errors.doseUnit ? t('dosing.form.errors.doseUnit') : undefined}
          />
        )}

        <Textarea
          id="dosing-log-notes"
          label={t('dosing.form.notes')}
          value={notes}
          maxLength={2000}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
          error={errors.notes ? t('dosing.logForm.notesError') : undefined}
        />

        <Inline gap="3" justify="end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('dosing.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isEdit ? t('dosing.form.submit') : t('dosing.logForm.submit')}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
